import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  Clock,
  Layers,
  PiggyBank,
  Search,
  ShieldCheck,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  useToast,
} from '@solara/ui-shared';
import { useI18n, useTranslation } from '@solara/i18n';
import type { RecoveryFindingDto } from '@solara/api-client';
import { useRecoveryFindings, useSetRecoveryStatus } from '@solara/api-client/react';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';
import { Download } from 'lucide-react';
import { saveCsv } from '../data/exports';

type RecoveryAuditStatus = RecoveryFindingDto['status'];

type TabKey = 'all' | 'open' | 'in-review' | 'recovered';

export default function RecoveryAuditPage() {
  const { t } = useTranslation('tickstar');
  const { locale } = useI18n();
  const { toast } = useToast();

  const aedFmt = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED', maximumFractionDigits: 0 }),
    [locale],
  );
  const numberFmt = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
    [locale],
  );

  const isRtl = locale === 'ar-AE';

  const [tab, setTab] = useState<TabKey>('all');
  const [selected, setSelected] = useState<RecoveryFindingDto | null>(null);

  const recoveryQuery = useRecoveryFindings();
  const setStatus = useSetRecoveryStatus();
  // Backend ships uppercase status (`OPEN`/`IN_REVIEW`/`RECOVERED`/`DISMISSED`) and
  // amounts as minor-unit BigInt strings. Normalise once at the top.
  const all = useMemo(() => {
    const STATUS_MAP: Record<string, RecoveryAuditStatus> = {
      OPEN: 'open',
      IN_REVIEW: 'in-review',
      RECOVERED: 'recovered',
      DISMISSED: 'dismissed',
      open: 'open',
      'in-review': 'in-review',
      recovered: 'recovered',
      dismissed: 'dismissed',
    };
    return (recoveryQuery.data ?? []).map((f) => {
      const raw = f as typeof f & {
        amountAedMinor?: string;
        recommendation?: string | null;
        detectedAt?: string;
        closedAt?: string | null;
      };
      const amountAed = raw.amountAedMinor
        ? (Number(raw.amountAedMinor) / 100).toString()
        : (f.amountAed ?? '0');
      return {
        ...f,
        status: (STATUS_MAP[(f.status as unknown as string) ?? 'open'] ?? 'open') as RecoveryAuditStatus,
        amountAed,
      };
    });
  }, [recoveryQuery.data]);

  const filtered = useMemo(() => {
    if (tab === 'all') return all;
    return all.filter((f) => f.status === tab);
  }, [all, tab]);

  const counts = useMemo(() => {
    const c: Record<RecoveryAuditStatus, number> = {
      open: 0,
      'in-review': 0,
      recovered: 0,
      dismissed: 0,
    };
    for (const f of all) c[f.status] += 1;
    return c;
  }, [all]);

  const totals = useMemo(() => {
    const open = all.filter((f) => f.status === 'open' || f.status === 'in-review');
    const recovered = all.filter((f) => f.status === 'recovered');
    return {
      totalOpen: open.length,
      pipelineAed: open.reduce((s, f) => s + (Number(f.amountAed) || 0), 0),
      recoveredAed: recovered.reduce((s, f) => s + (Number(f.amountAed) || 0), 0),
      topCategories: Object.entries(
        all.reduce((acc, f) => {
          acc[f.category] = (acc[f.category] ?? 0) + 1;
          return acc;
        }, {} as Record<string, number>),
      )
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [all]);

  async function handleAction(decision: 'recovered' | 'dismissed') {
    if (!selected) return;
    try {
      await setStatus.mutateAsync({ id: selected.id, status: decision });
      toast({
        title:
          decision === 'recovered'
            ? t('modules.recoveryAudit.status.recovered')
            : t('modules.recoveryAudit.status.dismissed'),
        description: `${selected.id} · ${aedFmt.format(Number(selected.amountAed) || 0)}`,
        tone: decision === 'recovered' ? 'success' : 'info',
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Update failed', tone: 'danger' });
    }
    setSelected(null);
  }

  function exportCsv() {
    if (all.length === 0) return;
    saveCsv(
      `solara-recovery-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      all.map((f) => ({
        id: f.id,
        category: f.category,
        status: f.status,
        amountAed: f.amountAed,
        supplier: (f as { supplier?: string }).supplier ?? '',
        invoiceId: (f as { invoiceId?: string }).invoiceId ?? '',
        recommendation: (f as { recommendation?: string | null }).recommendation ?? '',
        detectedAt: (f as { detectedAt?: string }).detectedAt ?? '',
      })),
      ['id', 'category', 'status', 'amountAed', 'supplier', 'invoiceId', 'recommendation', 'detectedAt'],
    );
    toast({ title: 'CSV exported', tone: 'success' });
  }

  if (recoveryQuery.isLoading) return <LoadingBlock />;
  if (recoveryQuery.error) return <ErrorBanner error={recoveryQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('modules.recoveryAudit.title')}
            </h1>
          </div>
          <p className="text-fg-muted text-sm mt-1">{t('modules.recoveryAudit.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} className="gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </header>

      {/* KPI grid */}
      <section className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={<Search className="h-5 w-5" />}
          tone="warning"
          label={t('modules.recoveryAudit.kpi.open')}
          value={numberFmt.format(totals.totalOpen)}
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          tone="brand"
          label={t('modules.recoveryAudit.kpi.pipeline')}
          value={aedFmt.format(totals.pipelineAed)}
        />
        <KpiCard
          icon={<PiggyBank className="h-5 w-5" />}
          tone="success"
          label={t('modules.recoveryAudit.kpi.recovered')}
          value={aedFmt.format(totals.recoveredAed)}
        />
        <Card className="rounded-2xl">
          <CardBody className="p-4 flex items-start gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-xl shrink-0 text-info bg-cyan-50">
              <Layers className="h-5 w-5" />
            </div>
            <div className="flex flex-col leading-tight min-w-0 flex-1">
              <span className="text-xs uppercase tracking-wide text-fg-subtle">
                {t('modules.recoveryAudit.kpi.categories')}
              </span>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {totals.topCategories.slice(0, 3).map((c) => (
                  <Badge key={c.category} tone="neutral" className="text-[10px]">
                    {t(`modules.recoveryAudit.category.${c.category}`)} · {numberFmt.format(c.count)}
                  </Badge>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>
      </section>

      {/* Tabs + table */}
      <Tabs value={tab} defaultValue="all" onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="all">
            {t('modules.recoveryAudit.tabs.all')}
            <Badge tone="neutral" className="ms-2">
              {numberFmt.format(all.length)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="open">
            {t('modules.recoveryAudit.tabs.open')}
            <Badge tone="warning" className="ms-2">
              {numberFmt.format(counts.open)}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="in-review">
            {t('modules.recoveryAudit.tabs.review')}
            <Badge tone="info" className="ms-2">
              {numberFmt.format(counts['in-review'])}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="recovered">
            {t('modules.recoveryAudit.tabs.recovered')}
            <Badge tone="success" className="ms-2">
              {numberFmt.format(counts.recovered)}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {filtered.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck className="h-6 w-6" />}
              title={t('modules.recoveryAudit.tabs.recovered')}
            />
          ) : (
            <Card className="rounded-2xl">
              <CardBody>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-fg-subtle">
                        <th className="text-start font-medium pb-2">
                          {t('modules.recoveryAudit.header.category')}
                        </th>
                        <th className="text-start font-medium pb-2">
                          {t('modules.recoveryAudit.header.supplier')}
                        </th>
                        <th className="text-end font-medium pb-2">
                          {t('modules.recoveryAudit.header.amount')}
                        </th>
                        <th className="text-start font-medium pb-2">
                          {t('modules.recoveryAudit.header.confidence')}
                        </th>
                        <th className="text-end font-medium pb-2">
                          {t('modules.recoveryAudit.header.age')}
                        </th>
                        <th className="text-start font-medium pb-2">
                          {t('modules.recoveryAudit.header.status')}
                        </th>
                        <th className="text-start font-medium pb-2">
                          {t('modules.recoveryAudit.header.action')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filtered.map((f) => (
                        <FindingRow
                          key={f.id}
                          finding={f}
                          onClick={() => setSelected(f)}
                          isRtl={isRtl}
                          t={t}
                          aedFmt={aedFmt}
                          numberFmt={numberFmt}
                          dateFmt={dateFmt}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Partnership note */}
      <Card className="rounded-2xl border-amber-100 bg-amber-50/50">
        <CardBody className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-amber-700/90">{t('modules.recoveryAudit.subtitle')}</p>
        </CardBody>
      </Card>

      {/* Detail dialog */}
      <Dialog
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
        size="lg"
        title={selected ? t(`modules.recoveryAudit.category.${selected.category}`) : ''}
        description={selected ? selected.supplier ?? '' : ''}
        footer={
          selected ? (
            <>
              <Button
                variant="outline"
                className="gap-2 text-fg-muted"
                onClick={() => handleAction('dismissed')}
              >
                <XCircle className="h-4 w-4" />
                {t('modules.recoveryAudit.status.dismissed')}
              </Button>
              <Button className="gap-2" onClick={() => handleAction('recovered')}>
                <CheckCircle2 className="h-4 w-4" />
                {t('modules.recoveryAudit.status.recovered')}
              </Button>
            </>
          ) : null
        }
      >
        {selected ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-3">
              <DetailItem
                label={t('modules.recoveryAudit.header.amount')}
                value={
                  <span className="text-lg font-semibold tabular-nums text-brand-700">
                    {aedFmt.format(Number(selected.amountAed) || 0)}
                  </span>
                }
              />
              <DetailItem
                label={t('modules.recoveryAudit.header.confidence')}
                value={<ConfidenceBadge confidence={confidenceLabel(selected.confidence)} />}
              />
              <DetailItem
                label={t('modules.recoveryAudit.header.status')}
                value={<StatusBadge status={selected.status} t={t} />}
              />
            </div>
            <DetailItem
              label={t('modules.recoveryAudit.header.category')}
              value={t(`modules.recoveryAudit.category.${selected.category}`)}
            />
            {selected.invoiceId ? (
              <DetailItem
                label="Invoice"
                value={
                  <Badge tone="neutral" className="font-mono text-[10px]">
                    {selected.invoiceId}
                  </Badge>
                }
              />
            ) : null}
            {selected.recommendation ? (
              <DetailItem
                label={t('modules.recoveryAudit.header.action')}
                value={
                  <p className="text-fg-muted leading-relaxed">{selected.recommendation}</p>
                }
              />
            ) : null}
            <div className="flex items-center gap-2 text-xs text-fg-subtle">
              <Clock className="h-3 w-3" />
              <span>
                {dateFmt.format(new Date(selected.detectedAt))} · {selected.id}
              </span>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function FindingRow({
  finding,
  onClick,
  t,
  aedFmt,
  numberFmt,
  dateFmt,
}: {
  finding: RecoveryFindingDto;
  onClick: () => void;
  isRtl?: boolean;
  t: (k: string, vars?: Record<string, unknown>) => string;
  aedFmt: Intl.NumberFormat;
  numberFmt: Intl.NumberFormat;
  dateFmt: Intl.DateTimeFormat;
}) {
  const amount = Number(finding.amountAed) || 0;
  const ageDays = Math.max(
    0,
    Math.floor((Date.now() - new Date(finding.detectedAt).getTime()) / 86_400_000),
  );
  void dateFmt;
  return (
    <tr
      className="cursor-pointer hover:bg-surface-muted transition-colors"
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <td className="py-2.5">
        <div className="text-sm font-medium">
          {t(`modules.recoveryAudit.category.${finding.category}`)}
        </div>
      </td>
      <td className="py-2.5 text-sm" dir="auto">
        {finding.supplier ?? '—'}
      </td>
      <td className="py-2.5 text-end text-sm font-semibold tabular-nums">
        {amount > 0 ? aedFmt.format(amount) : '—'}
      </td>
      <td className="py-2.5">
        <ConfidenceBadge confidence={confidenceLabel(finding.confidence)} />
      </td>
      <td className="py-2.5 text-end text-sm tabular-nums">{numberFmt.format(ageDays)}</td>
      <td className="py-2.5">
        <StatusBadge status={finding.status} t={t} />
      </td>
      <td className="py-2.5 text-xs text-fg-muted truncate max-w-[20rem]">
        {finding.recommendation ?? ''}
      </td>
    </tr>
  );
}

function confidenceLabel(n: number): 'low' | 'medium' | 'high' {
  if (n >= 0.75) return 'high';
  if (n >= 0.4) return 'medium';
  return 'low';
}

function ConfidenceBadge({ confidence }: { confidence: 'low' | 'medium' | 'high' }) {
  const tone: 'success' | 'info' | 'warning' =
    confidence === 'high' ? 'success' : confidence === 'medium' ? 'info' : 'warning';
  return <Badge tone={tone}>{confidence}</Badge>;
}

function StatusBadge({
  status,
  t,
}: {
  status: RecoveryAuditStatus;
  t: (k: string, vars?: Record<string, unknown>) => string;
}) {
  const tone: 'warning' | 'info' | 'success' | 'neutral' =
    status === 'open'
      ? 'warning'
      : status === 'in-review'
      ? 'info'
      : status === 'recovered'
      ? 'success'
      : 'neutral';
  return <Badge tone={tone}>{t(`modules.recoveryAudit.status.${status}`)}</Badge>;
}

function DetailItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-fg-subtle mb-1">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function KpiCard({
  icon,
  tone,
  label,
  value,
}: {
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'info' | 'warning' | 'danger';
  label: string;
  value: string;
}) {
  const accent: Record<typeof tone, string> = {
    brand: 'text-brand-600 bg-brand-50',
    success: 'text-success bg-emerald-50',
    info: 'text-info bg-cyan-50',
    warning: 'text-warning bg-amber-50',
    danger: 'text-danger bg-red-50',
  };
  const valueTone: Record<typeof tone, string> = {
    brand: '',
    success: 'text-success',
    info: '',
    warning: '',
    danger: 'text-danger',
  };
  return (
    <Card className="rounded-2xl">
      <CardBody className="p-4 flex items-start gap-3">
        <div className={cn('h-10 w-10 grid place-items-center rounded-xl shrink-0', accent[tone])}>
          {icon}
        </div>
        <div className="flex flex-col leading-tight min-w-0 flex-1">
          <span
            className="text-[11px] sm:text-xs uppercase tracking-wide text-fg-subtle leading-snug line-clamp-2"
            title={label}
          >
            {label}
          </span>
          <span className={cn('mt-1 text-lg sm:text-xl font-semibold tabular-nums break-words', valueTone[tone])}>{value}</span>
        </div>
      </CardBody>
    </Card>
  );
}
